import { marketApi, Site } from '@envato/foundation-types'
import * as classNames from 'classnames'
import * as React from 'react'
import * as titleize from 'titleize'

import { MakeSearchLink } from '../../../common/utils/location/types'
import { ILocationParams } from '../../../common/reducers/reactRouterLocation'
import configs from '../../../config/sites/categories'
import AutoSuggest from '../../components/autosuggest/'
import createAutoSuggestDataContainer from '../../components/autosuggest/autoSuggestDataContainer'
import createAutoSuggestLogicContainer from '../../components/autosuggest/autoSuggestLogicContainer'
import BreadCrumb from '../breadcrumb'
import { IMenu } from '../breadcrumb/breadcrumb'
import SafeInnerHtml from '../safeInnerHtml'
import TextLink from '../textLink'

import * as styles from './pageHeader.module.css'

interface IPropsDefault {
  heading: string
  currentSite: Site
  defaultBreadcrumbs?: IMenu[]
  topArticle?: string | null
  subHeading?: string
  makeBreadcrumbLink?: MakeSearchLink
  category?: string
}

interface IPropsWithoutSearch extends IPropsDefault {
  showAutoSuggest?: false
  locationParams?: ILocationParams
  makeSearchLink?: MakeSearchLink
}

interface IPropsWithSearch extends IPropsDefault {
  showAutoSuggest: true
  locationParams: ILocationParams
  makeSearchLink: MakeSearchLink
}

type Props = IPropsWithSearch | IPropsWithoutSearch

const AutoSuggestContainer = createAutoSuggestDataContainer(
  createAutoSuggestLogicContainer(AutoSuggest)
)

const buildBreadcrumbLinks = ({
  currentSite,
  category,
  makeBreadcrumbLink
}: {
  currentSite: Site
  category: string
  makeBreadcrumbLink: MakeSearchLink
}) => {
  const categoryArray = category.split('/')
  return [
    { name: 'Home', path: '/' },
    ...categoryArray.map((fragement, index) => {
      const pathName = category.substring(
        0,
        category.indexOf(categoryArray[index]) + categoryArray[index].length
      )
      return {
        name:
          configs[currentSite].reduce(
            (acc: string, curr: marketApi.ICategory) =>
              curr.path === fragement ? curr.name : acc,
            ''
          ) || titleize(fragement.replace('-', ' ')),
        path: makeBreadcrumbLink({ category: pathName }, currentSite)
      }
    })
  ]
}

const PageHeader = ({
  heading,
  subHeading,
  topArticle,
  category,
  currentSite,
  locationParams,
  showAutoSuggest,
  makeSearchLink,
  makeBreadcrumbLink = makeSearchLink,
  defaultBreadcrumbs = [{ name: 'Home', path: '/' }]
}: Props) => {
  const [isTopArticleExpanded, setTopAricleExpanded] = React.useState(false)
  const readMoreText = isTopArticleExpanded ? 'Read less' : 'Read more'

  React.useEffect(
    () => {
      setTopAricleExpanded(false)
    },
    [topArticle]
  )

  return (
    <div className={styles.root}>
      <div className={styles.wrapper}>
        <div className={styles.headerWrapper}>
          <div className={styles.breadcrumbs}>
            {category && makeBreadcrumbLink && (
              <BreadCrumb
                currentSite={currentSite}
                menus={buildBreadcrumbLinks({
                  currentSite,
                  category,
                  makeBreadcrumbLink
                })}
              />
            )}

            {!category && (
              <BreadCrumb
                menus={defaultBreadcrumbs}
                currentSite={currentSite}
              />
            )}
          </div>
          <h1 className={styles.heading}>{heading}</h1>
        </div>
        {showAutoSuggest && locationParams && makeSearchLink && (
          <div className={styles.searchWrapper}>
            <AutoSuggestContainer
              inputName="term"
              value={locationParams.term}
              placeholder="Search within this category"
              size="small"
              currentSite={currentSite}
              locationParams={locationParams}
              makeSearchLink={makeSearchLink}
            />
          </div>
        )}
      </div>
      <div className={styles.wrapper}>
        <div className={styles.headerWrapper}>
          {subHeading && <h2 className={styles.subHeading}>{subHeading}</h2>}
          {topArticle && (
            <TextLink
              onClick={() => setTopAricleExpanded(!isTopArticleExpanded)}
              className={styles.readMore}
            >
              {readMoreText}
            </TextLink>
          )}
          {topArticle && (
            <SafeInnerHtml
              className={classNames(
                'fds-c-text-preformatted',
                styles.topArticle,
                {
                  [styles.hide]: !isTopArticleExpanded,
                  [styles.show]: isTopArticleExpanded
                }
              )}
              html={topArticle}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default PageHeader
